import { useState } from "react";
import { FlowDiagram, TechTag, FadeIn, slug } from "./common";
import CategoryHeader from "./CategoryHeader";
import { CategoryBackground } from "./SciBackground";
import { ShieldCheck, Lock } from "lucide-react";

const QS_LAYERS = [
  { title: "Quality Systems Map", items: [
    { label: "Operational Issue", detail: "A deviation, excursion, trend or recurring loss observed in manufacturing or Product Quality." },
    { label: "Deviation / Investigation", detail: "The event is documented and scoped within the Quality system." },
    { label: "RCA", detail: "Scientific root-cause analysis establishes why, with converging evidence." },
    { label: "Risk Assessment", detail: "Patient, product and process impact evaluated before action." },
    { label: "CAPA", detail: "Corrective and preventive actions address the verified cause." },
    { label: "Change Control", detail: "Changes to the controlled state are reviewed, approved and traceable." },
    { label: "Documentation", detail: "SOPs, work instructions and records updated under document control." },
    { label: "Training", detail: "Affected personnel trained before the change governs their work." },
    { label: "Implementation", detail: "The approved change goes live with communication and support." },
    { label: "Effectiveness Verification", detail: "Performance monitored to confirm the action worked — and keeps working." },
  ]},
];

const ALCOA = [
  ["Attributable", "Who performed the activity?", "Harvested records retain source-system identity and retrieval context."],
  ["Legible", "Can the record be interpreted throughout its lifecycle?", "Structured schemas and consistent units replace ambiguous free-form exports."],
  ["Contemporaneous", "Was information recorded when the activity occurred?", "Source timestamps are preserved and validated — never back-filled."],
  ["Original", "Is source information retained or traceably reproduced?", "Immutable raw zones keep source records unmodified beside curated layers."],
  ["Accurate", "Does the record correctly represent the activity?", "Schema validation, range checks and reconciliation counts verify content."],
  ["Complete", "Is anything missing?", "Completeness audits and gap reports quantify what is absent rather than hiding it."],
  ["Consistent", "Does the record agree with itself over time?", "Chronological sorting and deterministic transformations keep ordering stable."],
  ["Enduring", "Will it survive?", "Mirror datasets, backups and logs outlast any single file or session."],
  ["Available", "Can it be retrieved when needed?", "Analysis-ready datasets make evidence queryable during investigations and audits."],
];

const INTEGRITY_FLOW = [
  {
    title: "Automation Designed Around Data Integrity",
    items: [
      { label: "Source Data", detail: "Unmodified source records — files, API responses, sensor telemetry." },
      { label: "Timestamp Validation", detail: "Contemporaneity checked before any processing." },
      { label: "Schema / Completeness Checks", detail: "Malformed or partial input quarantined with reason logged." },
      { label: "Deduplication", detail: "Natural keys prevent double-counted evidence." },
      { label: "Source Traceability", detail: "Every derived value carries lineage back to its origin." },
      { label: "Controlled Transformation", detail: "Deterministic, reproducible processing — reruns produce identical output." },
      { label: "Audit-Ready Output", detail: "Datasets with exception logs and completeness reports attached." },
    ],
  },
];

const REGULATED_AUTOMATION_FLOW = [
  {
    title: "Regulated Automation Principle",
    items: [
      { label: "Source Data", detail: "The system of record remains the authority." },
      { label: "Validation", detail: "Input is verified before it is trusted." },
      { label: "Traceability", detail: "Lineage from output back to source is preserved." },
      { label: "Controlled Transformation", detail: "Logic is versioned, deterministic and reviewable." },
      { label: "Exception Management", detail: "Failures surface visibly in queues — never silently dropped." },
      { label: "Verified Output", detail: "Results are checked against expectations before use." },
    ],
  },
];

const COMPLIANCE_ITEMS = [
  "cGMP / cGxP",
  "FDA-Regulated Manufacturing Principles",
  "21 CFR 820 / QMSR Awareness",
  "21 CFR Part 11 Awareness",
  "ISO 13485 / ISO 9001 Awareness",
  "GDP",
  "ALCOA+",
  "Data Integrity",
  "CAPA",
  "Deviation Management",
  "Change Control",
  "Validation / Qualification",
  "Risk Assessment",
  "Audit Readiness",
  "Training Compliance",
  "Material & Reagent Traceability",
];

const QS_AREAS = ["GMP", "CAPA", "Deviation", "RCA", "ECO / Change Control", "SOP / WI", "Document Control", "ALCOA+", "Data Integrity", "Process Risk", "FMEA", "Continuous Improvement"];

const MINDSET = [
  ["Traceable", "Every output can be followed back to its source records and logic."],
  ["Repeatable", "Same inputs, same outputs — deterministic reruns, checkpoint/resume."],
  ["Testable", "Outputs verified against known cases before trusted use."],
  ["Controlled", "Changes move through review, approval and versioning."],
  ["Secure", "No credentials in code; access follows system permissions."],
  ["Understandable", "A colleague can read the workflow and reconstruct its behaviour."],
  ["Maintainable", "Simple, modular, documented — survives its author."],
  ["Fit for Intended Use", "Scope matches the actual business need — no more, no less."],
];

const CSV_LIFECYCLE = [
  { title: "Automation Lifecycle in a Regulated Environment", items: [
    { label: "Business Need", detail: "The operational problem stated in process terms first." },
    { label: "User Requirement", detail: "What the tool must do for the people who depend on it." },
    { label: "Risk Assessment", detail: "What the tool may influence; controls scaled accordingly." },
    { label: "Design", detail: "Architecture, data model and controls defined before build." },
    { label: "Test", detail: "Structured testing and output verification against expectations." },
    { label: "User Acceptance", detail: "The people with the problem confirm the tool solves it." },
    { label: "Controlled Release", detail: "Deployment under change control with documentation." },
    { label: "Change Management", detail: "Enhancements and fixes follow the same controlled path." },
    { label: "Periodic Review", detail: "The tool is re-evaluated against its intended use over time." },
  ]},
];

const BA_STRIP = ["Requirements Gathering", "Current-State Analysis", "Future-State Design", "Process Mapping", "Use Cases", "Acceptance Criteria", "Data Mapping", "Stakeholder Alignment", "Testing", "Implementation"];

export default function QualitySystems() {
  return (
    <section className="relative overflow-hidden" data-testid="quality-systems-section">
      <CategoryBackground variant="quality" accent="teal" accent2="success" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
      <CategoryHeader
        id="quality-systems"
        category="product-quality"
        eyebrow="Product Quality & Quality Systems"
        title="Improvement travels through Quality — or it doesn't travel"
        description="Analytical performance, lot genealogy, yield intelligence and risk-based Product Quality investigation — GMP fluency across CAPA, deviation, change control and data integrity makes improvements deployable in regulated manufacturing."
      />
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 mt-10 items-start">
        <FlowDiagram layers={QS_LAYERS} testId="quality-systems-flow" />
        <div>
          <h3 className="font-sans tracking-tight text-lg text-paper mb-4">Areas of Expertise</h3>
          <div className="flex flex-wrap gap-1.5" data-testid="qs-expertise-chips">
            {QS_AREAS.map((a) => (
              <TechTag key={a}>{a}</TechTag>
            ))}
          </div>
          <div className="panel p-5 mt-6">
            <p className="overline-label">Business Analysis Capability</p>
            <p className="text-xs text-steel mt-2 mb-3 leading-relaxed">
              Manufacturing automation begins with understanding the actual process — not with writing software.
            </p>
            <div className="flex flex-wrap gap-1.5" data-testid="ba-capability-strip">
              {BA_STRIP.map((b) => (
                <span key={b} className="font-mono text-[10px] px-2 py-1 rounded-sm bg-navy border border-teal/30 text-pale/80">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-20" data-testid="digital-quality-section">
        <h3 className="font-sans tracking-tight text-2xl text-paper">Building Automation for Regulated Environments</h3>
        <p className="text-steel text-sm mt-2 max-w-3xl">
          A useful automation is not only technically correct. In a GxP environment it must also be:
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
          {MINDSET.map(([t, d], i) => (
            <FadeIn key={t} delay={(i % 4) * 0.05}>
              <div className="panel p-4 h-full" data-testid={`mindset-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <p className="font-sans text-sm font-semibold text-cyan">{t}</p>
                <p className="text-[11px] text-steel mt-2 leading-snug">{d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-8 mt-10 items-start">
          <FlowDiagram layers={CSV_LIFECYCLE} testId="validation-lifecycle" />
          <div className="panel p-5">
            <p className="overline-label">Stated Precisely</p>
            <ul className="mt-3 space-y-2">
              {[
                "cGMP / cGxP manufacturing experience",
                "GDP and data-integrity expertise",
                "ALCOA+ working application",
                "Change-control and deviation experience",
                "Quality Systems experience",
                "Requirements-definition experience",
                "Structured testing / output verification",
                "Source-data reconciliation",
                "UAT concepts",
                "CSV concepts",
                "21 CFR Part 11 awareness",
                "21 CFR 820 / QMSR awareness",
                "ISO 13485 / ISO 9001 awareness",
                "GAMP 5 working knowledge",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2 text-xs text-pale/80">
                  <span className="w-1 h-1 rounded-full bg-cyan mt-1.5 shrink-0" aria-hidden="true" /> {x}
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-steel mt-4 leading-relaxed">
              Capabilities are stated at working-knowledge level. No claim of CSV leadership, GAMP 5 expertise or Part 11 validation ownership is made.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-20" data-testid="compliance-section">
        <h3 className="font-sans tracking-tight text-2xl text-paper">Regulated Manufacturing &amp; Compliance</h3>
        <p className="text-steel text-sm mt-2 max-w-3xl leading-relaxed">
          Experience supporting Product Quality and manufacturing activities within regulated biotechnology environments where traceability,
          controlled documentation, data integrity, validated processes and objective evidence are critical to release and process decisions.
        </p>
        <div className="flex flex-wrap gap-1.5 mt-6" data-testid="compliance-items">
          {COMPLIANCE_ITEMS.map((c) => (
            <span key={c} className="font-mono text-[10px] px-2 py-1 rounded-sm bg-navy border border-teal/40 text-pale/85">
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-20" data-testid="alcoa-section">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-teal" aria-hidden="true" />
          <h3 className="font-sans tracking-tight text-2xl text-paper">ALCOA+ — Data Integrity as an Engineering Constraint</h3>
        </div>
        <p className="text-steel text-sm mt-2 max-w-3xl">
          Not a keyword — a design constraint. Select each element to see how the automation workflows on this site enforce it.
        </p>
        <AlcoaExplorer />
        <div className="mt-8">
          <FlowDiagram layers={INTEGRITY_FLOW} testId="data-integrity-flow" />
        </div>
      </div>

      <div className="mt-20 grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start" data-testid="regulated-automation">
        <div className="panel p-6 border-l-2 border-l-violet">
          <Lock className="w-5 h-5 text-violet" aria-hidden="true" />
          <p className="overline-label mt-3">The Differentiator</p>
          <p className="font-sans text-lg text-paper mt-3 leading-relaxed">
            Automation in regulated manufacturing is not simply about removing manual work.
          </p>
          <p className="text-sm text-pale/85 mt-3 leading-relaxed">
            The workflow must preserve source traceability, data integrity, exception visibility and reproducibility — otherwise the time saved
            today becomes the audit finding of tomorrow.
          </p>
        </div>
        <FlowDiagram layers={REGULATED_AUTOMATION_FLOW} testId="regulated-automation-flow" />
      </div>
      </div>
    </section>
  );
}

function AlcoaExplorer() {
  const [sel, setSel] = useState(0);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-9 gap-2 mt-6" data-testid="alcoa-grid">
      <div className="contents">
        {ALCOA.map(([term, q, how], i) => (
          <button
            key={term}
            data-testid={`alcoa-${slug(term)}`}
            onClick={() => setSel(i)}
            className={`panel p-3 text-left transition-colors ${sel === i ? "border-cyan bg-cyan/10" : "hover:border-cyan/40"}`}
          >
            <p className={`font-mono text-[11px] uppercase tracking-wider ${sel === i ? "text-cyan" : "text-pale/80"}`}>{term}</p>
            <p className="text-[10px] text-steel mt-1.5 leading-snug">{q}</p>
          </button>
        ))}
      </div>
      <div className="col-span-full panel p-4 border-l-2 border-l-teal" data-testid="alcoa-detail">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">How automation enforces it</p>
        <p className="text-sm text-pale/85 mt-2">{ALCOA[sel][2]}</p>
      </div>
    </div>
  );
}
